import { Col, Collapse, Row, Typography } from "antd";
import type { CollapseProps } from "antd";
import CollapseExpandIcon from "../CollapseExpandIcon";
import SelectComponent from "../SelectComponent";
import SwitchOptionComponent from "../SwitchComponent";
import InputComponent from "../InputComponent";
import RichTextComponent from "../RichTextComponent";
import { Culture } from "../../context/VolunteersHighlightContext/constants";
import {
	KVKK_APP_SELECT_OPTIONS,
	KVKK_PLATFORM_SELECT_OPTIONS,
	KvkkGeneralFormData,
	KvkkLocalizableFormData,
	KvkkRules,
} from "../../context/KvkkContext/constants";
import {
	GENERAL_SECTION_KEY,
	getTextSectionKey,
	KvkkFormKeys,
	KvkkGeneralTexts,
	KvkkSectionLabels,
	KvkkTextTexts,
} from "./constants";

export type KvkkLocalizableChanges = Partial<Pick<KvkkLocalizableFormData, "title" | "text">>;

export const applyKvkkLocalizableChanges = <T extends { culture: Culture }>(
	localizables: T[],
	culture: Culture,
	changes: KvkkLocalizableChanges
): T[] => localizables.map(item => (item.culture === culture ? { ...item, ...changes } : item));

interface KvkkFormProps {
	general: KvkkGeneralFormData;
	localizables: KvkkLocalizableFormData[];
	// On the add form the Turkish texts are required; the update form only sends what changed.
	isRequired: boolean;
	onGeneralChange: (changes: Partial<KvkkGeneralFormData>) => void;
	onLocalizableChange: (culture: Culture, changes: KvkkLocalizableChanges) => void;
}

const toSelectValue = (value?: number) => (value === undefined ? null : String(value));

// "Genel Bilgiler" with the platform, the application and the kind of the text, then one "KVKK Metni - <language>" section
// per culture with its title and its rich text.
const KvkkForm = ({ general, localizables, isRequired, onGeneralChange, onLocalizableChange }: KvkkFormProps) => {
	const items: CollapseProps["items"] = [
		{
			key: GENERAL_SECTION_KEY,
			label: KvkkSectionLabels.GENERAL,
			children: (
				<>
					{/* The 10px under the selects that a text field has under itself, so the switches do not stick to them. */}
					<Row gutter={[20, 16]} style={{ marginBottom: 10 }}>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SelectComponent
								title={KvkkGeneralTexts.platform.label}
								isRequired={isRequired}
								placeholder={KvkkGeneralTexts.platform.placeholder}
								options={KVKK_PLATFORM_SELECT_OPTIONS}
								value={toSelectValue(general.platform)}
								onChange={value => onGeneralChange({ platform: Number(value) })}
							/>
						</Col>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SelectComponent
								title={KvkkGeneralTexts.appId.label}
								isRequired={isRequired}
								placeholder={KvkkGeneralTexts.appId.placeholder}
								options={KVKK_APP_SELECT_OPTIONS}
								value={toSelectValue(general.appId)}
								onChange={value => onGeneralChange({ appId: Number(value) })}
							/>
						</Col>
					</Row>
					<Row gutter={[20, 16]}>
						<Col xs={24} md={16} lg={12}>
							<SwitchOptionComponent
								title={KvkkGeneralTexts.isPage.label}
								helperText={KvkkGeneralTexts.isPage.helperText}
								value={general.isPage}
								onChange={checked => onGeneralChange({ isPage: checked })}
							/>
						</Col>
						<Col xs={24} sm={12} md={8} lg={6}>
							<SwitchOptionComponent
								title={KvkkGeneralTexts.isManager.label}
								value={general.isManager}
								onChange={checked => onGeneralChange({ isManager: checked })}
							/>
						</Col>
					</Row>
				</>
			),
		},
		...localizables.map(localizable => {
			const texts = KvkkTextTexts[localizable.culture];
			const isCultureRequired = isRequired && localizable.culture === KvkkRules.requiredCulture;

			return {
				key: getTextSectionKey(localizable.culture),
				label: KvkkSectionLabels.getText(localizable.culture),
				children: (
					<Row gutter={[20, 16]}>
						<Col span={24}>
							<InputComponent
								title={texts.title.label}
								isRequired={isCultureRequired}
								placeholder={texts.title.placeholder}
								value={localizable.title}
								maxLength={KvkkRules.titleMaxLength}
								onChange={e => onLocalizableChange(localizable.culture, { title: e.target.value })}
							/>
						</Col>
						<Col span={24}>
							<Typography.Title level={5} style={{ color: "#2e333e" }}>
								{texts.text.label}
								{isCultureRequired && <span style={{ color: "red" }}>&nbsp;*</span>}
							</Typography.Title>
							<RichTextComponent
								resizable={false}
								messageTemplate={localizable.text}
								backgroundColor="transparent"
								placeholder={texts.text.placeholder}
								handleSetChangedTemplate={text => onLocalizableChange(localizable.culture, { text })}
							/>
						</Col>
					</Row>
				),
			};
		}),
	];

	return (
		<Collapse
			items={items}
			expandIconPosition="end"
			bordered={false}
			ghost
			expandIcon={CollapseExpandIcon}
			defaultActiveKey={KvkkFormKeys}
		/>
	);
};

export default KvkkForm;
